import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Loader2, AlertTriangle, Users, X, User, MapPin, Calendar, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import PersonCardMobile from '../components/PersonCardMobile';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SearchPage = () => {
  const [states, setStates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('all');
  const [searchGender, setSearchGender] = useState('all');
  const [searchType, setSearchType] = useState('all');
  const [missingResults, setMissingResults] = useState([]);
  const [unknownResults, setUnknownResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => { fetchStates(); }, []);

  const fetchStates = async () => {
    try { const response = await axios.get(`${API}/api/states`); setStates(response.data); }
    catch (error) { console.error('Error fetching states:', error); }
  };

  const handleSearch = async () => {
    if (!searchQuery && !searchCity && searchState === 'all' && searchGender === 'all') return;
    setLoading(true); setSearched(true);
    try {
      const params = {
        limit: '20',
        ...(searchQuery && { search: searchQuery }),
        ...(searchCity && { city: searchCity }),
        ...(searchState !== 'all' && { state: searchState }),
        ...(searchGender !== 'all' && { gender: searchGender })
      };
      const queryString = new URLSearchParams(params).toString();
      if (searchType === 'all' || searchType === 'missing') {
        const missingRes = await axios.get(`${API}/api/cases/missing?${queryString}`);
        setMissingResults(missingRes.data.cases);
      } else { setMissingResults([]); }
      if (searchType === 'all' || searchType === 'unknown') {
        const unknownRes = await axios.get(`${API}/api/cases/unknown?${queryString}`);
        setUnknownResults(unknownRes.data.cases);
      } else { setUnknownResults([]); }
    } catch (error) { console.error('Search error:', error); }
    finally { setLoading(false); }
  };

  const clearSearch = () => {
    setSearchQuery(''); setSearchCity(''); setSearchState('all');
    setSearchGender('all'); setSearchType('all');
    setMissingResults([]); setUnknownResults([]); setSearched(false);
  };

  const totalResults = missingResults.length + unknownResults.length;
  const getPhotoUrl = (path) => path ? (path.startsWith('http') ? path : `${API}/api/files/${path}`) : null;
  const genderMap = { 'male': 'पुरुष', 'female': 'महिला', 'other': 'अन्य' };

  return (
    <div className="pb-24 md:pb-12" data-testid="search-page">
      {/* ===== MOBILE HEADER ===== */}
      <div className="md:hidden bg-gray-900 text-white p-6 pb-8 rounded-b-[2rem]">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Search className="w-5 h-5" /></div>
          <div>
            <h1 className="font-outfit font-bold text-xl">केस खोजें</h1>
            <p className="text-white/70 text-sm">लापता और अज्ञात व्यक्तियों में खोजें</p>
          </div>
        </div>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input placeholder="नाम से खोजें..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} className="pl-12 h-14 rounded-2xl bg-white/90 border-0 text-gray-900 placeholder:text-gray-500 text-base" data-testid="search-query-input" />
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2"><X className="w-5 h-5 text-gray-400" /></button>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="शहर" value={searchCity} onChange={(e) => setSearchCity(e.target.value)} className="rounded-xl bg-white/90 border-0 text-gray-900 placeholder:text-gray-500" data-testid="search-city-input" />
            <Select value={searchState} onValueChange={setSearchState}>
              <SelectTrigger className="rounded-xl bg-white/90 border-0" data-testid="search-state-select"><SelectValue placeholder="राज्य" /></SelectTrigger>
              <SelectContent><SelectItem value="all">सभी राज्य</SelectItem>{states.map((state) => <SelectItem key={state} value={state}>{state}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={searchGender} onValueChange={setSearchGender}>
              <SelectTrigger className="rounded-xl bg-white/90 border-0" data-testid="search-gender-select"><SelectValue placeholder="लिंग" /></SelectTrigger>
              <SelectContent><SelectItem value="all">सभी लिंग</SelectItem><SelectItem value="male">पुरुष</SelectItem><SelectItem value="female">महिला</SelectItem><SelectItem value="other">अन्य</SelectItem></SelectContent>
            </Select>
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger className="rounded-xl bg-white/90 border-0" data-testid="search-type-select"><SelectValue placeholder="प्रकार" /></SelectTrigger>
              <SelectContent><SelectItem value="all">सभी</SelectItem><SelectItem value="missing">लापता</SelectItem><SelectItem value="unknown">अज्ञात</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={handleSearch} disabled={loading} className="w-full h-12 rounded-full bg-white text-gray-900 hover:bg-gray-100 font-medium" data-testid="search-submit-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Search className="w-5 h-5 mr-2" />}खोजें
          </Button>
        </div>
      </div>

      {/* ===== DESKTOP HEADER ===== */}
      <div className="hidden md:block py-8 border-b border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
              <Search className="w-7 h-7 text-gray-600" />
            </div>
            <div>
              <h1 className="font-outfit font-bold text-3xl text-gray-900">केस खोजें</h1>
              <p className="text-gray-500">लापता और अज्ञात व्यक्तियों में खोजें</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input placeholder="नाम से खोजें..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} className="pl-12 h-12 rounded-xl text-base" data-testid="desktop-search-query" />
            </div>
            <Input placeholder="शहर" value={searchCity} onChange={(e) => setSearchCity(e.target.value)} className="w-40 h-12 rounded-xl" data-testid="desktop-search-city" />
            <Select value={searchState} onValueChange={setSearchState}>
              <SelectTrigger className="w-44 h-12 rounded-xl"><SelectValue placeholder="सभी राज्य" /></SelectTrigger>
              <SelectContent><SelectItem value="all">सभी राज्य</SelectItem>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={searchGender} onValueChange={setSearchGender}>
              <SelectTrigger className="w-36 h-12 rounded-xl"><SelectValue placeholder="सभी लिंग" /></SelectTrigger>
              <SelectContent><SelectItem value="all">सभी</SelectItem><SelectItem value="male">पुरुष</SelectItem><SelectItem value="female">महिला</SelectItem></SelectContent>
            </Select>
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger className="w-36 h-12 rounded-xl"><SelectValue placeholder="सभी प्रकार" /></SelectTrigger>
              <SelectContent><SelectItem value="all">सभी</SelectItem><SelectItem value="missing">लापता</SelectItem><SelectItem value="unknown">अज्ञात</SelectItem></SelectContent>
            </Select>
            <Button onClick={handleSearch} disabled={loading} className="h-12 px-8 rounded-xl bg-gray-900 hover:bg-gray-800" data-testid="desktop-search-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Search className="w-5 h-5 mr-2" />}खोजें
            </Button>
            {searched && <Button variant="outline" onClick={clearSearch} className="h-12 rounded-xl"><X className="w-4 h-4 mr-2" />हटाएं</Button>}
          </div>
        </div>
      </div>

      {/* ===== RESULTS ===== */}
      <div className="px-4 md:px-0 md:py-8 -mt-4 md:mt-0">
        <div className="md:max-w-6xl md:mx-auto md:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gray-600" /></div>
          ) : searched ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600"><span className="font-semibold text-gray-900">{totalResults}</span> परिणाम मिले</p>
                {totalResults > 0 && <button onClick={clearSearch} className="text-sm text-red-600 font-medium md:hidden">साफ़ करें</button>}
              </div>

              {totalResults === 0 ? (
                <div className="text-center py-16 app-card"><Search className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-600 font-medium">कोई परिणाम नहीं मिला</p><p className="text-sm text-gray-400 mt-2">अलग खोज शब्द आज़माएं</p></div>
              ) : (
                <>
                  {/* Missing Results */}
                  {missingResults.length > 0 && (
                    <div>
                      <h2 className="font-outfit font-semibold text-lg text-gray-900 flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-red-600" />लापता ({missingResults.length})</h2>
                      {/* Mobile */}
                      <div className="md:hidden space-y-4">
                        {missingResults.map((person, index) => <PersonCardMobile key={person.case_id} person={person} type="missing" index={index} />)}
                      </div>
                      {/* Desktop Grid */}
                      <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {missingResults.map((person, index) => (
                          <motion.div key={person.case_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                            <Link to={`/case/${person.case_id}`}>
                              <div className="app-card card-hover overflow-hidden">
                                <div className="aspect-[4/5] bg-gray-100 relative">
                                  {person.photo_url ? <img src={getPhotoUrl(person.photo_url)} alt={person.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-16 h-16 text-gray-300" /></div>}
                                  <div className="absolute top-3 left-3"><span className="status-badge-glass px-3 py-1.5 text-xs font-bold rounded-full">लापता</span></div>
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-16">
                                    <h3 className="font-outfit font-bold text-lg text-white truncate">{person.name}</h3>
                                    <p className="text-white/80 text-sm">{person.age} वर्ष, {genderMap[person.gender] || person.gender}</p>
                                  </div>
                                </div>
                                <div className="p-4 space-y-2">
                                  <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin className="w-4 h-4 text-gray-400" /><span className="truncate">{person.city}, {person.state}</span></div>
                                  <div className="flex items-center gap-2 text-sm text-gray-600"><Calendar className="w-4 h-4 text-gray-400" /><span>{person.last_seen_date}</span></div>
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Unknown Results */}
                  {unknownResults.length > 0 && (
                    <div className="mt-6">
                      <h2 className="font-outfit font-semibold text-lg text-gray-900 flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-amber-600" />अज्ञात ({unknownResults.length})</h2>
                      <div className="md:hidden space-y-4">
                        {unknownResults.map((person, index) => <PersonCardMobile key={person.case_id} person={person} type="unknown" index={index} />)}
                      </div>
                      <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {unknownResults.map((person, index) => (
                          <motion.div key={person.case_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                            <Link to={`/case/${person.case_id}`}>
                              <div className="app-card card-hover overflow-hidden">
                                <div className="aspect-[4/5] bg-gray-100 relative">
                                  {person.photo_url ? <img src={getPhotoUrl(person.photo_url)} alt={person.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-16 h-16 text-gray-300" /></div>}
                                  <div className="absolute top-3 left-3"><span className="px-3 py-1.5 text-xs font-bold rounded-full bg-amber-500/80 backdrop-blur text-white">अज्ञात</span></div>
                                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-16">
                                    <h3 className="font-outfit font-bold text-lg text-white truncate">{person.name}</h3>
                                    <p className="text-white/80 text-sm">{person.age} वर्ष, {genderMap[person.gender] || person.gender}</p>
                                  </div>
                                </div>
                                <div className="p-4 space-y-2">
                                  <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin className="w-4 h-4 text-gray-400" /><span className="truncate">{person.city}, {person.state}</span></div>
                                  <div className="flex items-center gap-2 text-sm text-gray-600"><Calendar className="w-4 h-4 text-gray-400" /><span>{person.found_date}</span></div>
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><Search className="w-10 h-10 text-gray-300" /></div>
              <p className="text-gray-500 font-medium">खोजने के लिए ऊपर फ़िल्टर का उपयोग करें</p>
              <p className="text-sm text-gray-400 mt-2">नाम, शहर या राज्य से खोजें</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
